import { z } from 'zod';

// ============================================================
// Shared Zod schemas — Source of truth for PSN response shape.
//
// 對齊：
//   - app/serializers/product_serial_number_serializer.rb (list_item / detail / inventory_block)
//   - app/services/product_serial_number_timeline_builder.rb (timeline event shape)
//   - app/controllers/api/{admin,operator}/v1/product_serial_numbers_controller.rb
//
// 兩端 response shape 完全相同（PSN 端點無 admin/operator 分支序列化）：
//   - operator base_scope 強制 status='in_stock' + current_warehouse + accessible merchants，
//     僅影響「能查到哪些列」，不改變每列 shape
//   - 同樣的 list_item / detail / inventory_block / timeline 在兩端通用
//
// 因此 admin/operator service.ts 共用這份 schema，避免手動同步漂移。
// ============================================================

// PSN 主體 status（對齊 ProductSerialNumber enum；'disposed' 預留給未來 disposition flow）
export const ProductSerialNumberStatusSchema = z.enum([
  'in_stock',
  'shipped',
  'returned_pending',
  'disposed',
]);

// inventory_block — `inventory` 可能為 nil（shipped / returned_pending / disposed 沒綁庫存時）
// inventory.status 是 Inventory 的 status 欄位（非 PSN.status），passthrough 寬鬆處理
// inventory_type 是 { id, name } object（後端會回 InventoryType 的人類可讀名稱），inventory 沒綁
// InventoryType 時為 nil
export const ProductSerialNumberInventorySchema = z.object({
  id: z.string(),
  warehouse: z.object({
    id: z.string(),
    name: z.string(),
  }).passthrough(),
  batch: z.string().nullable().optional(),
  inventory_type: z
    .object({ id: z.string(), name: z.string() })
    .passthrough()
    .nullable()
    .optional(),
  status: z.string().nullable().optional(),
}).passthrough();

// list_item — get_records 每筆 / show 主體 shape
export const ProductSerialNumberRecordSchema = z.object({
  id: z.string(),
  number: z.string(),
  status: ProductSerialNumberStatusSchema,
  product: z.object({
    id: z.string(),
    name: z.string(),
    sku: z.string(),
  }).passthrough(),
  inventory: ProductSerialNumberInventorySchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
}).passthrough();

// timeline event ref — 不同 type 攜帶的欄位略有差異，這裡用最寬鬆的 union shape：
//   receipt:     { type: 'inbound',         id, code }
//   fulfillment: { type: 'fulfillment',     id, code, order_id, order_code, parcel_id }
//   return:      { type: 'return_receipt',  id, code }
//   disposition: ref === null
// builder 對 fulfillment_events 使用 compact，所以 nil 欄位會被剝掉 — optional 處理。
export const ProductSerialNumberTimelineRefSchema = z.object({
  type: z.enum(['inbound', 'fulfillment', 'return_receipt']),
  id: z.string().optional(),
  code: z.string().optional(),
  order_id: z.string().optional(),
  order_code: z.string().optional(),
  parcel_id: z.string().nullable().optional(),
}).passthrough();

export const ProductSerialNumberTimelineEventTypeSchema = z.enum([
  'receipt',
  'fulfillment',
  'return',
  'disposition',
]);

export const ProductSerialNumberTimelineEventSchema = z.object({
  type: ProductSerialNumberTimelineEventTypeSchema,
  event_at: z.string(),
  ref: ProductSerialNumberTimelineRefSchema.nullable(),
  // 僅 disposition 事件帶 disposition: 'restock' | 'scrap'
  disposition: z.enum(['restock', 'scrap']).optional(),
}).passthrough();

// show 端點 — list_item.merge(timeline:)
export const ProductSerialNumberDetailSchema = ProductSerialNumberRecordSchema.extend({
  timeline: z.array(ProductSerialNumberTimelineEventSchema),
}).passthrough();

// ============================================================
// Exported types — caller / hook / component 可以 import
// ============================================================

export type ProductSerialNumberStatus = z.infer<typeof ProductSerialNumberStatusSchema>;
export type ProductSerialNumberInventory = z.infer<typeof ProductSerialNumberInventorySchema>;
export type ProductSerialNumberRecord = z.infer<typeof ProductSerialNumberRecordSchema>;
export type ProductSerialNumberTimelineEventType =
  z.infer<typeof ProductSerialNumberTimelineEventTypeSchema>;
export type ProductSerialNumberTimelineEvent =
  z.infer<typeof ProductSerialNumberTimelineEventSchema>;
export type ProductSerialNumberDetail = z.infer<typeof ProductSerialNumberDetailSchema>;
